/**
 * Boundary-coercion helpers for reading untyped values — DB JSON columns
 * (`inputData`, `misc`, `metadata`, `generationInput`, …), webhook payloads, and external
 * API responses — into the narrow shapes our code and zod schemas expect.
 *
 * Lives in jsutils (zero runtime deps, frontend + backend safe) so every layer reads JSON the
 * same way. These exist because a bare `value as string | undefined` cast LIES: a JSON column or
 * third-party payload can hold `null`, a number, an object, etc., and the cast lets that
 * wrong-typed value leak downstream. When it later flows into a tRPC `.output()` validator
 * (`z.string().optional()`), a single malformed row 500s the whole endpoint (VM-828: one
 * `templateId: null` rejected all 200 gallery rows). Parse at the boundary instead of asserting.
 */

/**
 * Coerce an unknown value (DB JSON field / external payload) into `string | undefined`.
 * Anything that isn't a string — `null`, numbers, objects, missing — normalizes to
 * `undefined`. Use this for every read of a JSON-column field that is typed
 * `string | undefined`, instead of a lying `as string | undefined` cast.
 */
export const asOptionalString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined)

/**
 * Coerce an unknown value into something safe to interpolate into a string (log line, UI label,
 * monitor payload). Scalars stringify normally; `null`/`undefined`/empty fall back; objects are
 * JSON-serialized rather than becoming the useless `"[object Object]"`.
 *
 * The empty-string check preserves `||`-chain semantics at the call sites this replaced
 * (`config.name || "?"`), where a blank value should fall through to the fallback.
 */
export const asDisplayString = (value: unknown, fallback = ""): string => {
  if (value === null || value === undefined) return fallback
  if (typeof value === "string") return value || fallback
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") return String(value)
  try {
    const serialized = JSON.stringify(value)
    // JSON.stringify returns undefined for functions/symbols.
    return serialized === undefined || serialized === "{}" ? fallback : serialized
  } catch {
    // Circular reference — nothing useful to show, so use the caller's fallback.
    return fallback
  }
}

/**
 * Extract a human-readable message from an unknown thrown value.
 *
 * Replaces the repo's old `error instanceof Error ? error.message : String(error)` idiom, whose
 * `String(error)` branch collapsed any non-Error object throw (e.g. a provider's
 * `{ code: 500, detail: "..." }`) into `"[object Object]"`, discarding the only diagnostic
 * information the log line carried.
 */
export const asErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  return asDisplayString(error, "Unknown error")
}
